'use client';

import { HStack, VStack } from '@terra/ui-styled-system';
import { ShieldEnergyIcon } from '@terra/ui-icons';
import { Text } from '../../../components/styled/text';
import type {
  PromptOption,
  GuardrailConfirmationMessage,
} from '@terra/shared-dto-chat-types';
import { createElement, useState } from 'react';
import { MarkdownPreview } from '../../markdown-preview';
import { useTranslations } from 'next-intl';
import { getToolConfig } from './tool-config';
import { formatToolInputPayload } from './tool-input-utils';
import { MinimalToolCard } from './MinimalToolCard';
import type { HITLStatus } from './hitl-types';

export function GuardrailConfirmationPromptCard({
  dataMessage,
  selectedValue,
  onDataMessageEvent,
}: {
  selectedValue?: string;
  dataMessage?: GuardrailConfirmationMessage;
  onDataMessageEvent?: (event: {
    dataMessage: GuardrailConfirmationMessage;
    selectedOption: PromptOption;
    message?: string;
  }) => void;
}) {
  const t = useTranslations(
    'designSystem.chat.guardrailConfirmationPromptCard',
  );
  const guardrailConfirmationPrompt = dataMessage?.guardrailConfirmationPrompt;
  const toolName = guardrailConfirmationPrompt?.toolName;
  const toolArgs = guardrailConfirmationPrompt?.args;
  const reason = guardrailConfirmationPrompt?.reason;
  const [isPending, setIsPending] = useState(false);

  if (!guardrailConfirmationPrompt) return null;
  const config = getToolConfig(toolName);
  const toolIcon = createElement(config.icon, { size: 12 });
  const formattedCode = formatToolInputPayload({
    formatPayload: config.formatPayload,
    input: toolArgs,
  });
  const approveOption = guardrailConfirmationPrompt.options.find(
    (opt) => opt.value.toLowerCase() === 'approve',
  );
  const rejectOption = guardrailConfirmationPrompt.options.find(
    (opt) => opt.value.toLowerCase() === 'reject',
  );
  const isDecided = !!selectedValue;
  const isApproved = selectedValue?.toLowerCase() === 'approve';
  const hitlStatus: HITLStatus = isDecided
    ? isApproved
      ? 'approve'
      : 'reject'
    : 'pending';
  const handleApprove = () => {
    if (isPending || isDecided || !approveOption || !dataMessage) return;
    setIsPending(true);
    onDataMessageEvent?.({ dataMessage, selectedOption: approveOption });
  };

  const handleReject = () => {
    if (isPending || isDecided || !rejectOption || !dataMessage) return;
    setIsPending(true);
    onDataMessageEvent?.({ dataMessage, selectedOption: rejectOption });
  };

  return (
    <MinimalToolCard
      icon={toolIcon}
      toolName={config.name}
      status="pending"
      hitlStatus={hitlStatus}
      onApprove={!isDecided ? handleApprove : undefined}
      onReject={rejectOption && !isDecided ? handleReject : undefined}
      disabled={isPending || isDecided}
      defaultExpanded
    >
      <VStack alignItems="start" gap={3} py={2} width="100%">
        {/* Blocked by Guardrail banner */}
        <VStack
          alignItems="start"
          gap={1.5}
          width="100%"
          backgroundColor="red.1"
          borderRadius="md"
          padding={3}
          borderWidth="1px"
          borderColor="red.5"
        >
          <HStack gap={2} alignItems="center">
            <ShieldEnergyIcon size={16} color="var(--colors-red-11)" />
            <Text fontSize="sm" fontWeight="bold" color="red.11">
              {t('blockedByGuardrail')}
            </Text>
          </HStack>
          {reason && (
            <Text fontSize="sm" color="red.11">
              {reason}
            </Text>
          )}
        </VStack>

        {/* Formatted code / payload */}
        {formattedCode && (
          <VStack alignItems="start" gap={1.5} width="100%">
            <Text fontSize="xs" fontWeight="semibold" color="fg.muted">
              {t('code')}
            </Text>
            <MarkdownPreview source={formattedCode} />
          </VStack>
        )}
      </VStack>
    </MinimalToolCard>
  );
}
